import { useState } from "react";
import { Upload, FileSpreadsheet, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  applyRowsToSnapshot,
  generateAgentConfigTemplateCsv,
  generateAgentConfigTemplateXlsx,
  parseAgentConfigFile,
  type AgentConfigSnapshot,
} from "@/lib/agent-config-export";

type AgentConfigImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSnapshot: AgentConfigSnapshot;
  onApply: (snapshot: AgentConfigSnapshot) => void;
};

export function AgentConfigImportDialog({
  open,
  onOpenChange,
  currentSnapshot,
  onApply,
}: AgentConfigImportDialogProps) {
  const { toast } = useToast();
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<{
    applied: number;
    skipped: string[];
    snapshot: AgentConfigSnapshot;
  } | null>(null);

  const reset = () => {
    setPreview(null);
    setParsing(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = (format: "csv" | "xlsx") => {
    if (format === "csv") {
      downloadBlob(generateAgentConfigTemplateCsv(), "template-config-agente.csv");
    } else {
      downloadBlob(generateAgentConfigTemplateXlsx(), "template-config-agente.xlsx");
    }
    toast({
      title: "Template baixado",
      description: "Edite a coluna valor e envie o arquivo aqui para importar.",
    });
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const rows = await parseAgentConfigFile(file);
      if (rows.length === 0) {
        throw new Error("Nenhuma linha de configuração encontrada no arquivo.");
      }
      const result = applyRowsToSnapshot(currentSnapshot, rows);
      setPreview({
        applied: result.appliedKeys.length,
        skipped: result.skipped,
        snapshot: result.snapshot,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Erro ao ler o arquivo.";
      setPreview(null);
      toast({ title: "Erro na importação", description: message, variant: "destructive" });
    } finally {
      setParsing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar configurações
          </DialogTitle>
          <DialogDescription>
            Use o template com as colunas <span className="font-medium">Seção</span>,{" "}
            <span className="font-medium">O que configurar</span> e{" "}
            <span className="font-medium">Sua resposta</span>. Preencha em linguagem simples — listas com
            uma linha por item, sim/não para ligar opções. Linhas em branco não alteram o agente. Depois de
            aplicar, clique em <span className="font-medium">Salvar</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center hover:bg-muted/50">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium">
              {parsing ? "Lendo arquivo…" : "Selecionar CSV ou XLSX"}
            </span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              className="sr-only"
              disabled={parsing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void handleFile(f);
              }}
            />
          </label>

          {preview ? (
            <Alert>
              <AlertDescription className="text-sm space-y-1">
                <p>
                  <span className="font-medium">{preview.applied}</span> campo(s) reconhecido(s) e prontos para
                  aplicar.
                </p>
                {preview.skipped.length > 0 ? (
                  <p className="text-muted-foreground">
                    Ignorados ({preview.skipped.length}): {preview.skipped.slice(0, 4).join(", ")}
                    {preview.skipped.length > 4 ? "…" : ""}
                  </p>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-2 border-t pt-3">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => handleDownloadTemplate("csv")}>
              <Download className="h-3.5 w-3.5" />
              Template CSV
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => handleDownloadTemplate("xlsx")}>
              <Download className="h-3.5 w-3.5" />
              Template XLSX
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!preview}
            onClick={() => {
              if (preview) {
                onApply(preview.snapshot);
                handleClose(false);
              }
            }}
          >
            Aplicar ao agente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
